import * as React from 'react';
import { useSetRecoilState } from 'recoil';
import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { CmdToast } from '@commander-services/gui-components';
import { CmdToastButton } from '@commander-services/gui-components/build/components/CmdToast';
import { useLocation } from 'wouter';
import Config from '../../../config';
import LoaderService from '../../Services/LoaderService';
import Bounds from '../OnlineMapNew/GoogleMapLayer/Bounds';
import GoogleMap from '../OnlineMapNew/GoogleMapLayer';
import ToolbarLayer from '../OnlineMapNew/ToolbarLayer';
import { localForageStore } from '../../store/localForageStore';
import PanelsLayer from './PanelsLayer';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { ICheckedTableItems, IFilterTripsOverview } from '../TripsOverviewNew/interfaces';
import {
  IRide,
  IRidePlayerSpeedLimitsSettings,
  IRidePositionsApiResponseData,
} from '../RidePlayer/types';
import {
  enableArrowsAtom,
  enableSpeedlimitsAtom,
  enableWaitingsAtom,
  playerSpeedAtom,
  showSelectRidesModalAtom,
  speedlimitSettingsAtom,
} from '../RidePlayer/RidePlayerState';
import {
  getMultiRidesPositions,
  getRidePositions,
  loadSettings,
} from '../RidePlayer/RidePlayerService';
import { isRidePlayerOpenAtom } from '../OnlineMapNew/OnlineMapState';

export type ICheckedTableItemsAndFilter = ICheckedTableItems & IFilterTripsOverview;

function TripMap() {
  const { formatMessage: f } = useIntl();
  const location = useLocation();
  useDocumentTitle(f({ id: 'ridePlayer.title' }));
  const [googleMap, setGoogleMap] = React.useState<google.maps.Map | undefined>();
  const setEnableSpeedlimits = useSetRecoilState<boolean>(enableSpeedlimitsAtom);
  const setEnableWaitings = useSetRecoilState<boolean>(enableWaitingsAtom);
  const setEnableArrows = useSetRecoilState(enableArrowsAtom);
  const setSpeedLimits = useSetRecoilState<IRidePlayerSpeedLimitsSettings>(speedlimitSettingsAtom);
  const setPlayerSpeed = useSetRecoilState<number>(playerSpeedAtom);
  const [rides, setRides] = React.useState<IRide[] | null>(null);
  const setShowSelectRidesModal = useSetRecoilState<boolean>(showSelectRidesModalAtom);
  const setIsRidePlayerOpen = useSetRecoilState<number | boolean>(isRidePlayerOpenAtom);
  const [showManualRideWarning, setShowManualRideWarning] = React.useState<
    false | [number, number]
  >(false);
  const { data: settingsData } = useQuery({
    queryKey: ['multiplayerSettings'],
    queryFn: () => loadSettings(),
    staleTime: 9000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const handleManualRide = (data: IRidePositionsApiResponseData) => {
    let manualRides = 0;
    Object.values(data).forEach((ride) => {
      if (ride.positions && ride.positions.length === 0) {
        // if (!ride.positions || ride.positions.length === 0) {
        manualRides += 1;
      }
    });
    if (manualRides > 0) {
      setShowManualRideWarning([manualRides, Object.keys(data).length]);
    }
  };

  React.useEffect(() => {
    setIsRidePlayerOpen(true);

    if (settingsData) {
      setEnableWaitings(settingsData.showPause);
      setEnableArrows(settingsData.rideRenderType === 'line_arrow');
      setEnableSpeedlimits(settingsData.speedLimits.enabled);
      setSpeedLimits({
        minimumSpeed: settingsData.speedLimits.limits[0],
        warningSpeed: settingsData.speedLimits.limits[1],
        maximumSpeed: settingsData.speedLimits.limits[2],
      });
      setPlayerSpeed(settingsData.playerSpeed);
    }

    // @todo Use react-query
    const loadRides = async () => {
      const rideFromUrl = location[0].split('/')[2];
      let ridesToLoad: string[] = [];
      const sortRideIdbyCustomerIds: { [key: string]: string[] } = {};

      let ridesData: IRidePositionsApiResponseData | null = null;
      if (rideFromUrl) {
        ridesToLoad = [rideFromUrl];
      } else {
        ridesToLoad = (await localForageStore.getItem<string[]>('rides')) || [];
      }
      // console.log('ridesToLoadFromIndexedDb: ', ridesToLoadFromIndexedDb);
      // if (ridesToLoadFromIndexedDb.length > 0) {
      //   ridesToLoad = ridesToLoadFromIndexedDb;
      // }
      const multiRidesToLoad =
        (await localForageStore.getItem<ICheckedTableItemsAndFilter>('multi-rides')) || null;
      LoaderService.showLoader();

      if (multiRidesToLoad && !rideFromUrl) {
        ridesData = await getMultiRidesPositions(multiRidesToLoad);
        if (ridesData) {
          handleManualRide(ridesData);
        }
      } else {
        ridesToLoad.forEach((item) => {
          const itemSplit = item.split('-');
          if (!sortRideIdbyCustomerIds[itemSplit[0]]) {
            sortRideIdbyCustomerIds[itemSplit[0]] = [];
          }
          sortRideIdbyCustomerIds[itemSplit[0]].push(String(itemSplit[1]));
        });
        ridesData = await getRidePositions(sortRideIdbyCustomerIds);
      }

      if (ridesData) {
        const ridesArray = Object.values(ridesData);
        // Check for continuous rides
        const rideIndex = ridesArray.length - 1;
        const newRides = ridesArray.map((ride: IRide) => {
          const previousRideIndex = rideIndex - 1;
          if (
            ridesData &&
            ridesData[previousRideIndex] &&
            ridesData[previousRideIndex].rideData.id === ride.rideData.prevRideId &&
            ride.positions[0].type === 'START'
          ) {
            ridesData[previousRideIndex].positions.splice(-1, 1);
            ride.positions[0].type = 'START_STOP_PAUSE';
            return ride;
          }
          return ride;
        });
        setRides(newRides);
      }
      LoaderService.showLoader(false);
    };

    loadRides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseManualRideWarningToast = () => {
    setShowManualRideWarning(false);
  };

  const handleShowRideSelectModal = () => {
    setShowSelectRidesModal(true);
    setShowManualRideWarning(false);
  };

  const buttons: CmdToastButton[] = [
    {
      title: f({ id: 'tripsOverview.multiple.rides.manual.warning.button.understand' }),
      style: 'link',
      onClickCallback: handleCloseManualRideWarningToast,
      type: 'close',
    },
    {
      title: f({ id: 'tripsOverview.multiple.rides.manual.warning.button.selectRides' }),
      style: 'solid',
      onClickCallback: handleShowRideSelectModal,
      type: 'submit',
    },
  ];

  return (
    <>
      {showManualRideWarning && (
        <CmdToast
          id="toast"
          title={f(
            { id: 'tripsOverview.multiple.rides.manual.warning.title' },
            { manualRides: showManualRideWarning[0], totalRides: showManualRideWarning[1] }
          )}
          message={f({ id: 'tripsOverview.multiple.rides.manual.warning' })}
          type="warning"
          duration={null}
          onClose={handleCloseManualRideWarningToast}
          buttons={buttons}
        />
      )}
      <div
        id="google-map-fullscreen"
        style={{ width: '100%', height: '100%', position: 'absolute', overflow: 'hidden' }}
      >
        <GoogleMap googleMap={googleMap} setGoogleMap={setGoogleMap} />
        {/* {rides && rides.length > 0 && <MultiRidePanel rides={rides} />} */}
        {/* {Config.Player.debug.showPositionIndex && rides && rides.length > 0 && (
          <RidePlayerDebugPanel rides={rides} />
        )} */}
        <Bounds googleMap={googleMap} />
        {rides && <PanelsLayer googleMap={googleMap} rides={rides} />}
        {googleMap && (
          <ToolbarLayer
            googleMap={googleMap}
            vehicleFocusControl={Config.onlineMap.toolbar.vehicleFocusControl}
            autofocusControl={Config.onlineMap.toolbar.autoFocusControl}
          />
        )}
      </div>
    </>
  );
}

export default TripMap;
